import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, Scroll, RouterEvent } from '@angular/router';

// ionic
import { AlertController, IonContent } from '@ionic/angular';
import { PopoverController } from '@ionic/angular';
import { Events } from '../../helpers/events';

// rxjs
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// components
import { AttendeeFilterComponent } from "../../components/attendee-filter/attendee-filter.component";

// services
import { PaginatorService } from "../../services/paginator.service";
import { PreviewService } from '../../services/preview.service';

// models
import { Container } from "../../models/container";
import { Event } from "../../models/event";
import { Feed } from "../../models/feed";
import { Group } from "../../models/group";
import { Keyword } from "../../models/keyword";
import { User } from "../../models/user";
import { Participant } from "../../models/participant";
import { CustomLabel } from "../../models/custom-label";
import { AttendeesTab } from "../../models/attendees-tab";

// extends
import { BasePage } from "../base";

// config
import { environment } from "../../../environments/environment";

@Component({
    selector: 'app-preview',
    templateUrl: '../dashboard/dashboard.page.html',
    styleUrls: ['../dashboard/dashboard.page.scss'],
})

export class PreviewPage extends BasePage implements OnInit, OnDestroy {

    @ViewChild(IonContent) content: IonContent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * user
     *
     * @type {User}
     */
    public user: User;

    /**
     * active event for user
     *
     * @type Event
     */
    public event: Event;

    /**
     * Suggested keywords
     *
     * @type Keyword[]
     */
    public suggestedKeywords: Keyword[] = [];

    /**
     * Suggested keywords by others
     *
     * @type Keyword[]
     */
    public suggestedKeywordsByOthers: Keyword[] = [];

    /**
     * Existing keywords
     *
     * @type Keyword[]
     */
    public existingKeywords: Keyword[] = [];

    /**
     * bookmarks filter
     *
     * @type {boolean}
     */
    public bookmarks: boolean = false;

    /**
     * likes filter
     *
     * @type {boolean}
     */
    public likes: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public dataLoading: boolean = false;

    /**
     * status of dropdown boxex
     *
     * @type {boolean}
     */
    public appointmentBox: boolean = false;
    public notificationBox: boolean = false;

    /**
     * selected segment
     *
     * @type {String}
     */
    public segment: string = 'info';

    /**
     * selected view
     *
     * @type {String}
     */
    public view: string = 'matching';

    /**
     * object with block list
     *
     * @type {}
     */
    public blocks: any[];

    /**
     * participant has some keyword setting
     *
     * @type {boolean}
     */
    public savedKeywords: boolean = false;

    /**
     * participant has some keyword setting
     *
     * @type {boolean}
     */
    public visibleAllKeywords: boolean = false;

    /**
     * visible participants
     *
     * @type Participant
     */
    public visibleAttendees: Participant[] = [];

    /**
     * search string for attendees list
     *
     * @type {String}
     */
    public attendeesSearch: string = '';

    /**
     * how many appointments should be visible on dashboard
     *
     * @type Participant
     */
    public appointmentsLimit: number = 3;

    /**
     * height of the iframe
     *
     * @type string
     */
    public frameHeight: number = 800;

    attendeesTotal: number = 0;
    attendeesLimit: number = 10;
    attendeesOffset: number = 0;
    attendeesSort: string = '';

    attendeesLimitValues: { value: number, name: string }[] = [
        {
            value: 10,
            name: "10"
        },
        {
            value: 20,
            name: "20"
        },
        {
            value: 40,
            name: "40"
        },
        {
            value: 80,
            name: "80"
        },
        {
            value: 1000000,
            name: "All"
        }
    ];

    pager: any;

    page: number = 1;

    public homeUrlSegment: string = 'preview';

    /**
     * show header state
     *
     * @type {boolean}
     */
    public showHeader: boolean = false;

    public viewType: string = 'lines';

    public containers: Container[] = [];

    // handlers for events
    filterHandler;
    attendeeDetailHandler;
    eventChangeHandler

    /**
     * disable preview behaviour
     *
     * @type {boolean}
     */
    public kyewordsEmpty: boolean = true;

    /**
     * info for user about live calculation
     *
     * @type {boolean}
     */
    public calculatingCalls: number = 0;

    public scrollTop: number;

    public useDefaultEvent: boolean = true;

    public countSizes = {
        2: [1, 2],
        3: [1, 2, 3],
        4: [1, 2, 3, 4],
        5: [1, 2, 3, 4, 5]
    };

    showSharing: boolean = false;

    /**
     * is limited view for member list
     *
     * @type boolean
     */
    public limitedMemberList: boolean = false;

    /**
     * constructor
     *
     * @param appEvents
     * @param popoverController
     * @param alertCtrl
     * @param paginatorService
     * @param previewService
     */
    constructor(
        public appEvents: Events,
        public route: ActivatedRoute,
        public popoverController: PopoverController,
        public alertCtrl: AlertController,
        public paginatorService: PaginatorService,
        public previewService: PreviewService,
    ) {
        super();

        this.isPreview = true;

        this.viewType = !this.plt.sizeSm ? 'grid' : 'lines';

        // user language
        this.userLang = this.translate.currentLang;
        this.segment = this.route.snapshot.paramMap.get('segment');
        this.view = this.route.snapshot.paramMap.get('view');
        this.useDefaultEvent = JSON.parse(this.route.snapshot.queryParams.useDefaultEvent || true);

        this.router.events.pipe(
            takeUntil(this.ngUnsubscribe)
        ).subscribe((event: any) => {
            // for route event event get route event
            if ((event instanceof Scroll)) {
                event = <RouterEvent>event.routerEvent;
            }

            // using url from NavigationEnd instance, because route.snapshot was not sometimes updated...
            if (event instanceof RouterEvent && event instanceof NavigationEnd) {
                let params = event.url.replace(this.plt.defaultLink + '/preview/', '').split('/');
                if (this.segment != params[0]) {
                    this.segment = params[0];
                    // view param
                    this.view = params[1];
                    this.savedKeywords = !(params[1] == 'manage-keywords');

                    // refresh page content
                    // this.refresh();

                    // scroll to top
                    this.content.scrollToTop(0);
                }
            }
        });

        if (this.routerExtService.emptyHistory()) {
            // this.routerExtService.softNavigate(this.plt.defaultLink + '/preview/' + this.segment + '/' + (this.view ? this.view : ''));
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        // event of changing styles from backend
        window.addEventListener('message', (event) => {
            if (this.event && event.data && event.data.type == 'preview') {

                this.translate.use(event.data.lang);

                if (!this.useDefaultEvent || event.data.event.translations) {
                    this.event.translations = event.data.event.translations;
                }

                if (!this.useDefaultEvent || (event.data.event.lat && event.data.event.lng)) {
                    this.event.lat = event.data.event.lat;
                    this.event.lng = event.data.event.lng;
                }

                if (!this.useDefaultEvent || event.data.event.no_address) {
                    this.event.no_address = event.data.event.no_address;
                }

                if (!this.useDefaultEvent || event.data.event.default_locale) {
                    this.event.default_locale = event.data.event.default_locale;
                    this.event.containers.forEach((container) => {
                        container.translations[0].locale = this.event.default_locale;
                        container.keywords.forEach((keyword) => {
                            keyword.translations[0].locale = this.event.default_locale;
                        });
                    });
                }

                if (!this.useDefaultEvent || event.data.event.custom_labels) {
                    this.event.custom_labels = [];
                    event.data.event.custom_labels.forEach((label) => {
                        this.event.custom_labels.push(new CustomLabel(label));
                    });
                }

                if (!this.useDefaultEvent || event.data.event.starts_at) {
                    this.event.starts_at = event.data.event.starts_at;
                }

                if (!this.useDefaultEvent || event.data.event.ends_at) {
                    this.event.ends_at = event.data.event.ends_at;
                }

                if (!this.useDefaultEvent || event.data.event.logo_url) {
                    this.event.logo_url = event.data.event.logo_url;
                }

                if (!this.useDefaultEvent || event.data.event.public_email) {
                    this.event.public_email = event.data.event.public_email;
                }

                if (!this.useDefaultEvent || event.data.event.public_phone) {
                    this.event.public_phone = event.data.event.public_phone;
                }

                if (!this.useDefaultEvent || event.data.event.address) {
                    this.event.address = event.data.event.address;
                }

                if (!this.useDefaultEvent || event.data.event.is_community) {
                    this.event.is_community = event.data.event.is_community;
                }

                if (!this.useDefaultEvent || event.data.event.zip) {
                    this.event.zip = event.data.event.zip;
                }

                if (!this.useDefaultEvent || event.data.event.city) {
                    this.event.city = event.data.event.city;
                }

                if (!this.useDefaultEvent || event.data.event.homepage_content) {
                    this.event.homepage_content = event.data.event.homepage_content;
                }

                this.setupEvent(this.event);

                this.appEvents.publish('custom:design', {
                    type: 'organizer-preview',
                    setting: event.data.settings,
                    event: this.event,
                    participant: this.user.selected_participant
                });
            }
        });

        // load data
        this.previewService.preview().subscribe((user: User) => {
            if (user.id) {
                this.user = new User(user);
                this.refresh();
            } else {
                this.user = null;
            }
        });

        // sync state of filter from component
        this.appEvents.subscribe('attendee:filter', ((data) => {
            this.likes = data.likes;
            this.bookmarks = data.bookmarks;
            this.attendeesOffset = 0
            this.refreshAttendees();
        }));

        // detect changes on attendee, like bookmark/like in popup and sycn state
        this.appEvents.subscribe('attendee:changed', ((attendee) => {
            // find attendee in actual list and change his data
            this.visibleAttendees.forEach((item) => {
                if (item.id == attendee.id) {
                    item.liked = attendee.liked;
                    item.bookmarked = attendee.bookmarked;
                }

            });
        }));
    }

    /**
     * after view init
     *
     * @return void
     */
    ngAfterViewInit() {
        if (this.user && this.user.id) {
            this.refresh();
        }
    }

    /**
     * refresh page content
     *
     * @return void
     */
    public refresh($event?) {
        this.dataLoading = true;
        switch (this.segment) {
            case 'info':
            case 'detail':
                this.refreshDashboard($event);
                this.refreshAttendees($event);
                break;
            case 'attendees':
                this.refreshAttendees($event);
                break;
        }

        window.parent.postMessage({
            type: 'do-preview'
        }, '*');
    }

    /**
     * refresh dashboard
     *
     * @return void
     */
    public refreshDashboard($event?) {

        if (this.user.selected_participant && this.user.selected_participant.event) {
            let event = new Event(this.user.selected_participant.event);
            this.setupEvent(event);

            // hide loading
            if ($event) {
                $event.target.complete();
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        } else {
            if ($event) {
                $event.target.complete();
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        }

        // load most matching participants
        if (this.user.selected_participant && this.user.selected_participant.keywords && !!this.user.selected_participant.keywords.length) {
        } else {
            // redirect to managing keyword if not
            if (this.savedKeywords) {
                this.savedKeywords = false;
                // this.router.navigate(['/preview/info/manage-keywords']);
            }
        }

    }

    /**
     * setup event
     *
     * @param {Event} event
     */
    setupEvent(event: Event): void {

        // copy last selected group for this event...
        let group;
        let attendeesTab;
        if (this.event) {
            group = this.event.selectedGroup;
            attendeesTab = this.event.selectedAttendeesTab;
        }
        this.event = new Event(event, this.user);

        if (group) {
            this.event.selectedGroup = group;
            this.event.selectedAttendeesTab = attendeesTab;
        }

        this.initBlocks();

        // containers (and keywords)
        if (this.event.containers) {

            this.setupContainers();

            if (this.view != 'manage-keywords') {
                this.savedKeywords = this.user.selected_participant.keywords && !!this.user.selected_participant.keywords.length;
            }
        }
    }

    private setupContainers() {
        this.suggestedKeywords = [];
        this.containers = [];

        if (this.event.containers) {
            for (let c = 0; c < this.event.containers.length; c++) {
                const container = new Container(this.event.containers[c]);
                this.event.containers[c] = container;
                let keywordIncluded = false;
                // keywords
                if (container.keywords) {
                    for (let k = 0; k < container.keywords.length; k++) {
                        const keyword = container.keywords[k];

                        if (this.user.selected_participant.keywords.length
                            && this.user.selected_participant.keywords.find((item) => { return keyword.id == item.id })) {
                            keywordIncluded = true;
                        }

                        // suggested keywords
                        if (keyword.participant_id === this.user.selected_participant_id && !keyword.approved_at) {
                            this.suggestedKeywords.push(keyword);
                        }
                        // suggested keywords by others
                        else if (keyword.participant_id !== this.user.selected_participant_id && !keyword.approved_at) {
                            this.suggestedKeywordsByOthers.push(keyword);
                        }
                        // existing keywords
                        else {
                            this.existingKeywords.push(keyword);
                        }
                    }
                }

                // order keywords
                container.orderKeywordsByName(this.userLang, this.event.default_locale);

                // check if some keyword is checked, then include it in visible list
                if (keywordIncluded) {
                    this.containers.push(container);
                }
            }
        }
    }

    /**
     * Toggle keyword
     *
     * @param column
     * @param keyword
     */
    public toggleKeyword(column: number, keyword: Keyword) {
        return false;
    }

    /**
     * Enable keyword for a specific column
     *
     * @param column
     * @param keyword
     */
    public enableKeyword(column: number, keyword: Keyword) {
        return false;
    }

    /**
     * Disable keyword for a specific column
     *
     * @param column
     * @param keyword
     */
    public disableKeyword(column: number, keyword: Keyword) {
        return false;
    }

    /**
     * Check, if the user has activated a keyword for a specific column
     *
     * @param column
     * @param keyword
     * @returns {boolean}
     */
    public isEnabled(column: number, keyword: Keyword): boolean {
        if (this.user && this.user.selected_participant && this.user.selected_participant.keywords) {
            for (let k = 0; k < this.user.selected_participant.keywords.length; k++) {
                const item = this.user.selected_participant.keywords[k];
                if (keyword.id === item.id && column === item.pivot.column) {
                    return true;
                }
            }
        }

        return false;
    }

    async presentSuggestKeyword(ev: any, container) {
        return false;
    }

    /**
     * Add keyword (only if it doesn't exists yet)
     *
     * @param value
     * @param containerId
     */
    public suggestKeyword(value: string, containerId: number) {
        return false;
    }

    /**
     * Delete keyword
     *
     * @param keyword
     */
    public deleteSuggestedKeyword(keyword: Keyword) {
        return false;
    }

    public async openDeleteSuggest(keyword: Keyword) {
        return false;
    }

    /**
     * Deletes an object from the server
     *
     * @param keyword
     */
    public deleteKeywordFromServer(keyword: Keyword) {
        return false;
    }


    /**
     * show details about feed notification
     *
     * @param {Feed} feed
     */
    public openFeedDetail(feed: Feed): void { }

    /**
     * refresh attendees list
     *
     * @return void
     */
    public refreshAttendees($event?, reset = true) {
        if (this.user.selected_participant && this.user.selected_participant.event) {
            let event = new Event(this.user.selected_participant.event);
            this.setupEvent(event);

            if (reset) {
                this.visibleAttendees = [];
            }

            let counter = 0;
            this.user.selected_participant.event.participants.forEach((participant) => {
                if (++counter <= 10) {
                    this.visibleAttendees.push(new Participant(participant));
                }
            });
            this.attendeesTotal = 40;

            this.pager = this.paginatorService.getPager(this.attendeesTotal, this.page, this.attendeesLimit);


            // hide loading
            if ($event) {
                $event.target.complete();
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        }

    }

    /**
     * reset attendee filters
     *
     * @return void
     */
    public resetAttendeeForm() {
        this.likes = false;
        this.bookmarks = false;
        this.attendeesOffset = 0;
        this.attendeesSearch = '';
    }

    /**
     * set actual page for attendee list
     *
     * @param {number} page
     *
     * @return void
     */
    public setAttendeePage(page: number) {
        if (page < 1 || page > this.pager.totalPages) {
            return;
        }

        this.page = page;

        // get pager object from service
        this.pager = this.paginatorService.getPager(this.attendeesTotal, this.page, this.attendeesLimit);
        this.attendeesOffset = this.pager.startIndex;

        this.refreshAttendees();
    }

    /**
     * load more attendees in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.attendeesOffset += this.attendeesLimit;
        this.refreshAttendees(event, false);
    }

    /**
     * select group to show attendees of group
     *
     * @param {Group} group
     */
    public groupSelect(group: Group) {
        if (!this.loading) {
            this.event.selectedGroup = group;
            this.refresh();
        }
    }

    /**
     * select attendees tab to show attendees
     *
     * @param {AttendeesTab} tab
     */
    public attendeesTabSelect(tab: AttendeesTab) {
        if (!this.loading) {
            this.event.selectedAttendeesTab = tab;
            this.refresh();
        }
    }

    /**
     * show popover with attendees filter
     *
     * @param type
     *
     * @return void
     */
    public changeViewType(type: string) {
        this.viewType = type;
    }

    /**
     * show popover with attendees filter
     *
     * @param event
     *
     * @return void
     */
    async showFilter(ev: any) {
        const popover = await this.popoverController.create({
            component: AttendeeFilterComponent,
            event: ev,
            cssClass: 'attendee:filter',
            componentProps: {
                'sort': this.attendeesSort,
                'likes': this.likes,
                'bookmarks': this.bookmarks,

            }
        });

        await popover.present();
    }

    /**
     * Toggle like
     *
     * @param participant
     */
    public toggleLike($event, participant: Participant) {
        return false;
    }

    /**
     * Toggle bookmark
     *
     * @param participant
     */
    public toggleBookmark($event, participant: Participant) {
        return false;
    }

    /**
     * rediret to chat with selected attendee
     *
     */
    public goToMessages($event, attendee) {
        return false;
    }

    /**
     * Init block for event detail page
     *
     */
    public initBlocks() {
        this.blocks = [
            {
                key: 'description',
                title: 'DASHBOARD_EVENT_INFO_DESCRIPTION',
                opened: true
            },
            {
                key: "agenda",
                title: 'DASHBOARD_EVENT_INFO_AGENDA'
            },
            {
                key: "speaker",
                title: 'DASHBOARD_EVENT_INFO_SPEAKER'
            },
            {
                key: "partners",
                title: 'DASHBOARD_EVENT_INFO_PARTNERS'
            },
            {
                key: "other",
                title: 'DASHBOARD_EVENT_INFO_OTHER'
            },
            // {
            //     key: "directions",
            //     customLabel: {},
            //     defaultIcon: 'location.svg'
            // }
        ];
        for (let block of this.blocks) {
            // custom icons for tabs
            if (this.event.custom_labels) {
                this.event.custom_labels.forEach((customLabel) => {
                    if (block.key == customLabel.key && (customLabel.icon || (customLabel.translations && customLabel.translations.length > 0))) {
                        block.customLabel = customLabel;
                        if (block.customLabel.translate(this.userLang, this.event.default_locale).name) {
                            block.title = block.customLabel.translate(this.userLang, this.event.default_locale).name;
                        }
                    }
                });
            }
        }
    }

    /**
     * Method to change visible segment
     *
     * @param event
     */
    public tabChanged(segment) {
        this.segment = segment;
        // this.routerExtService.softNavigate(this.plt.defaultLink + '/preview/' + this.segment);

        setTimeout(() => {
            this.frameHeight = (window.innerHeight - 300);
            if (this.frameHeight < 650) {
                this.frameHeight = 650;
            }
        }, 500);
    }

    /**
     * Method to change visible block in event detail
     *
     * @param keyword
     */
    public blockChanged(blockSelected) {
        let original = blockSelected.opened;
        for (let block of this.blocks) {
            block.opened = false;
        }
        blockSelected.opened = !original;
    }

    /**
     * Deletes an object from the server
     *
     * @param key
     */
    public onLoad(key: String) {
        setTimeout(() => {
            this.event.custom_labels.forEach((customLabel) => {
                if (customLabel.key == key) {
                    customLabel.loadedIframe = true
                }
            });
        }, 500);
    }

    logScrolling(e) {

    }

    public async showCommunityFilter() {

    }

    public clearExtendedFilter() {
    }

    public async showEventDetail(block) {

    }

    public async openNotifications() {

    }

    public changeLoading(value: boolean) {
        this.loading = value;
    }

    public changeDataLoading(value: boolean) {
        this.dataLoading = value;
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
